"use client"

import * as React from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import {
  ChurchRegistrationForm,
  type ChurchRegistrationValues,
} from "./church-registration-form"
import { useRegisterChurchMutation } from "../services/create-church-query"

const FORM_ID = "register-church-dialog"

type RegisterChurchDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Registering a church as a dialog — the host for someone who already has one.
 *
 * Adding a second church is an errand, not an onboarding, so it belongs where "which church am I in" is
 * already answered: the workspace switcher. Same form as the full-page host; the submit lives in
 * `DialogFooter` and reaches the form through native `form="id"` association, so neither host needs the form
 * to know where it is rendered.
 */
export function RegisterChurchDialog({ open, onOpenChange }: RegisterChurchDialogProps) {
  const t = useTranslations("createChurch")
  const navigate = useNavigate()
  const registerChurch = useRegisterChurchMutation()
  const [canSubmit, setCanSubmit] = React.useState(false)

  const handleSubmit = async (values: ChurchRegistrationValues) => {
    const created = await registerChurch.mutateAsync(values)
    toast.success(t("cta.created", { name: created.name }))
    onOpenChange(false)
    // Switch to what they just made — creating a church and staying in the old one is the bug, not the feature.
    navigate(`/${created.slug}`)
  }

  return (
    <Dialog open={open} onOpenChange={registerChurch.isPending ? () => undefined : onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("subtitle")}</DialogDescription>
        </DialogHeader>

        {/* No padding wrapper: `dialog-body` already applies px-4 py-4, and adding more inset it twice. */}
        <ChurchRegistrationForm
          formId={FORM_ID}
          onSubmit={handleSubmit}
          onValidityChange={setCanSubmit}
          disabled={registerChurch.isPending}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={registerChurch.isPending}
          >
            {t("cta.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={!canSubmit} isLoading={registerChurch.isPending}>
            {t("cta.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
